import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, UserPlus, Ticket, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useBusinessMetrics } from '../../hooks/useBusinessMetrics';
import { baseApi } from '../../api/baseClient';
import CountUp from '../ui/CountUp';

const formatNumber = (num: number | undefined): string => {
  if (typeof num !== 'number' || isNaN(num)) return '0';
  return new Intl.NumberFormat('es-MX').format(num);
};

// Pulso semanal desde lealify-base: GET /analytics/dashboard (campo pulse) +
// GET /analytics/trends (serie diaria) para el sparkline de visitas.
interface DashboardResponse {
  pulse: {
    visits: { now: number; prev: number };
    new_contacts: { now: number; prev: number };
    redemptions: { now: number };
  };
}
interface TrendsResponse {
  series: Array<{ day: string; visits: number }>;
}

interface PulseData {
  visits7: number;
  visitsPrev: number;
  spark: number[];
  newClients7: number;
  newClientsPrev: number;
  newClientsCapped: boolean;
  redemptions7: number | null;
}

// Δ de un KPI vs el periodo anterior: verde sube, rojo baja, gris similar
const Delta: React.FC<{ current: number; previous: number; mode: 'percent' | 'abs' }> = ({ current, previous, mode }) => {
  const diff = current - previous;
  if (diff === 0 || (mode === 'percent' && previous === 0 && current === 0)) {
    return <p className="text-xs text-content-3 mt-0.5">— similar a la anterior</p>;
  }
  const up = diff > 0;
  let label: string;
  if (mode === 'percent') {
    label = previous > 0
      ? `${Math.abs(Math.round((diff / previous) * 100))}% vs semana pasada`
      : `${formatNumber(current)} esta semana`;
  } else {
    label = `${Math.abs(diff)} ${up ? 'más' : 'menos'} que la anterior`;
  }
  return (
    <p className={`text-xs font-semibold mt-0.5 ${up ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
      {up ? '▲' : '▼'} {label}
    </p>
  );
};

const WeeklyPulsePanel: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { metrics, loading: metricsLoading } = useBusinessMetrics(currentUser?.businessId, currentUser?.timeZone);

  const [data, setData] = useState<PulseData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.businessId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const [dashRes, trendsRes] = await Promise.allSettled([
        baseApi.get<DashboardResponse>('/analytics/dashboard'),
        baseApi.get<TrendsResponse>('/analytics/trends?days=30'),
      ]);

      if (cancelled) return;

      if (dashRes.status !== 'fulfilled') {
        console.error('[WeeklyPulse] dashboard:', dashRes.reason);
        setLoading(false);
        return;
      }
      const p = dashRes.value.pulse;

      // Sparkline: visitas de los últimos 7 días de la serie diaria.
      let spark: number[] = [];
      if (trendsRes.status === 'fulfilled') {
        spark = (trendsRes.value.series || []).slice(-7).map(d => Number(d.visits) || 0);
      } else {
        console.error('[WeeklyPulse] trends:', trendsRes.reason);
      }

      setData({
        visits7: p.visits.now,
        visitsPrev: p.visits.prev,
        spark,
        newClients7: p.new_contacts.now,
        newClientsPrev: p.new_contacts.prev,
        newClientsCapped: false,
        redemptions7: p.redemptions.now,
      });
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser?.businessId]);

  if (loading && !data) {
    return (
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Esta semana</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-skeleton rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const sparkMax = Math.max(...data.spark, 1);

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Esta semana</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        {/* Visitas 7d con sparkline */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/latest-visits')}
          className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 relative overflow-hidden hover:border-accent transition-colors"
        >
          <p className="text-xs text-content-3 flex items-center"><MapPin className="h-3.5 w-3.5 mr-1" /> Visitas (7 días)</p>
          <p className="text-2xl font-display font-extrabold text-content mt-1"><CountUp value={data.visits7} format={formatNumber} /></p>
          <Delta current={data.visits7} previous={data.visitsPrev} mode="percent" />
          <div className="absolute right-3 bottom-3 flex items-end gap-0.5 h-6" aria-hidden="true">
            {data.spark.map((v, i) => (
              <span
                key={i}
                className={`w-1.5 rounded-sm ${i === data.spark.length - 1 ? 'bg-accent' : 'bg-accent/25'}`}
                style={{ height: `${Math.max(12, (v / sparkMax) * 100)}%` }}
              />
            ))}
          </div>
        </button>

        {/* Clientes nuevos 7d */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/latest-registrations')}
          className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors"
        >
          <p className="text-xs text-content-3 flex items-center"><UserPlus className="h-3.5 w-3.5 mr-1" /> Clientes nuevos</p>
          <p className="text-2xl font-display font-extrabold text-content mt-1"><CountUp value={data.newClients7} format={formatNumber} />{data.newClientsCapped && data.newClients7 >= 50 ? '+' : ''}</p>
          <Delta current={data.newClients7} previous={data.newClientsPrev} mode="abs" />
        </button>

        {/* Canjes 7d */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/rewards')}
          className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors"
        >
          <p className="text-xs text-content-3 flex items-center"><Ticket className="h-3.5 w-3.5 mr-1" /> Canjes (7 días)</p>
          <p className="text-2xl font-display font-extrabold text-content mt-1">{data.redemptions7 === null ? '—' : <CountUp value={data.redemptions7} format={formatNumber} />}</p>
          <p className="text-xs text-content-3 mt-0.5">recompensas entregadas</p>
        </button>

        {/* Clientes totales */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/clients')}
          className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors"
        >
          <p className="text-xs text-content-3 flex items-center"><Users className="h-3.5 w-3.5 mr-1" /> Clientes totales</p>
          <p className="text-2xl font-display font-extrabold text-content mt-1">{metricsLoading ? '…' : <CountUp value={metrics?.uniqueCustomers ?? 0} format={formatNumber} />}</p>
          <p className="text-xs text-content-3 mt-0.5">en tu programa</p>
        </button>
      </div>
    </div>
  );
};

export default WeeklyPulsePanel;
